import fetch from "cross-fetch";
import { createInterfaceId } from "@gitlab/needle";
//#region src/fetch.ts
/**
* Controls transport behavior for one fetch/WebSocket operation.
*
* `inherit` keeps the process, system, and user proxy configuration that the
* language server resolved at startup. `direct` bypasses those proxy agents for
* the request while preserving TLS options such as custom CAs or certificate
* validation overrides. The option is advisory in browser environments where
* proxy resolution is owned by the host runtime.
*/
/**
* Structured WebSocket connection options describing the *intent*
* (proxy URL, TLS material, pre-built agent) for a WebSocket connection.
*
* Browser implementations return `undefined` and let the built-in
* WebSocket constructor handle its own networking.
*
* Node implementations populate this from the same proxy/TLS state that
* `Fetch` uses for HTTP. Consumers should use `toIsomorphicWsOptions`
* (exported from `@gitlab-org/fetch`) to flatten this to the
* `isomorphic-ws` / `ws` constructor option shape, which selects the
* correct fields for the current runtime.
*/
const LsFetch = createInterfaceId("LsFetch");
var FetchBase = class {
	updateRequestInit(method, init) {
		if (typeof init === "undefined") init = { method };
		else init.method = method;
		return init;
	}
	async initialize() {}
	async destroy() {}
	async fetch(input, init, _options) {
		return fetch(input, init);
	}
	async *streamResponse(_response, _cancellationToken) {
		throw new Error("Not implemented");
	}
	async delete(input, init, options) {
		return this.fetch(input, this.updateRequestInit("DELETE", init), options);
	}
	async get(input, init, options) {
		return this.fetch(input, this.updateRequestInit("GET", init), options);
	}
	async post(input, init, options) {
		return this.fetch(input, this.updateRequestInit("POST", init), options);
	}
	async put(input, init, options) {
		return this.fetch(input, this.updateRequestInit("PUT", init), options);
	}
	async patch(input, init, options) {
		return this.fetch(input, this.updateRequestInit("PATCH", init), options);
	}
	async head(input, init, options) {
		return this.fetch(input, this.updateRequestInit("HEAD", init), options);
	}
	async fetchBase(input, init, _options) {
		return (typeof global === "undefined" ? self : global).fetch(input, init);
	}
	getWebSocketOptions(_input, _options) {}
	updateAgentOptions(_opts) {}
};
//#endregion
//#region src/constants.ts
const REQUEST_TIMEOUT_MILLISECONDS = 15e3;
//#endregion
//#region src/extract_url.ts
function extractURL(input) {
	if (input instanceof URL) return input.toString();
	if (typeof input === "string") return input;
	return input.url;
}
//#endregion
//#region src/fetch_timeout_error.ts
var FetchTimeoutError = class extends Error {
	constructor(url) {
		const timeoutInSeconds = Math.round(REQUEST_TIMEOUT_MILLISECONDS / 1e3);
		super(`Request to ${extractURL(url)} timed out after ${timeoutInSeconds} second${timeoutInSeconds === 1 ? "" : "s"}`);
	}
};
//#endregion
//#region src/fetch_invalid_instance_version_error.ts
var InvalidInstanceVersionError = class extends Error {};
//#endregion
//#region src/websocket_options.ts
/**
* Local copy to avoid a circular dependency between `@gitlab-org/fetch`
* and `@gitlab-org/core` (`isBunRuntime`).
*
* Must be safe in the browser bundle where `process.versions` is undefined.
*/
function isBunRuntime() {
	return typeof process !== "undefined" && typeof process.versions !== "undefined" && typeof process.versions.bun === "string";
}
/**
* `readyState` values, fixed by the WHATWG WebSocket spec and shared by
* every implementation (`ws`, the browser's native `WebSocket`).
*/
const WebSocketReadyState = {
	CONNECTING: 0,
	OPEN: 1,
	CLOSING: 2,
	CLOSED: 3
};
/**
* Provides the WebSocket client class for the current runtime. Which class
* is correct is runtime-specific (Bun hijacks the literal `ws` specifier;
* browsers must use the platform `WebSocket`), so implementations are
* registered per entrypoint and injected — no runtime-specific import may
* appear in this shared module, or it gets bundled into browser consumers.
*
* Exposed as a class rather than a `create` method because some libraries
* (anycable's `websocketImplementation`) need the constructor itself.
*/
const WebSocketFactory = createInterfaceId("WebSocketFactory");
/**
* Translates structured WebSocket options to the flat option shape that the
* pure-JS `ws` client accepts: `{ agent, ca, cert, key, rejectUnauthorized }`.
* The shape is runtime-independent because the injected implementation is
* the same client on Node and Bun.
*
* `proxyUrl` is deliberately not translated: proxying is handled by the
* `agent` (`Fetch.getWebSocketOptions()` populates a `ProxyAgent` whenever
* proxy env vars are present). It is still consumed by
* `getDroppedOptionsWarnings` to surface the Bun TLS-in-TLS advisory.
*/
function toIsomorphicWsOptions(options) {
	const result = {};
	if (options.agent) result.agent = options.agent;
	if (options.tls) {
		const { ca, cert, key, rejectUnauthorized } = options.tls;
		if (ca !== void 0) result.ca = ca;
		if (cert !== void 0) result.cert = cert;
		if (key !== void 0) result.key = key;
		if (rejectUnauthorized !== void 0) result.rejectUnauthorized = rejectUnauthorized;
	}
	return result;
}
/**
* Returns human-readable warning strings for WebSocket transport limitations
* under the current runtime. Callers log these before connecting.
*
* Only one residual Bun gap is covered: an `https://`-scheme proxy URL, which
* means the proxy agent opens a TLS connection to the proxy itself and then
* tunnels the target's TLS inside it. Bun still stalls that TLS-in-TLS
* handshake (the inner handshake completes at byte level but `secureConnect`
* never fires). Every other scheme is left alone: `http://` CONNECT proxies
* work, including the TLS-inspecting MITM proxies used for enterprise SSL
* inspection, and SOCKS proxies never wrap the tunnel in TLS either.
*/
function getDroppedOptionsWarnings(options) {
	if (!isBunRuntime()) return [];
	const warnings = [];
	if (options.proxyUrl?.startsWith("https://")) warnings.push("An https:// proxy URL is configured. Bun cannot yet tunnel TLS through a TLS connection to the proxy itself, so the connection may hang. Use an http:// proxy URL if possible.");
	return warnings;
}
//#endregion
export { toIsomorphicWsOptions as a, REQUEST_TIMEOUT_MILLISECONDS as c, isBunRuntime as i, FetchBase as l, WebSocketReadyState as n, InvalidInstanceVersionError as o, getDroppedOptionsWarnings as r, FetchTimeoutError as s, WebSocketFactory as t, LsFetch as u };

//# sourceMappingURL=src-sUhLoNe4.mjs.map